/* eslint-env browser */
// Add / edit a vehicle type and its fares (wave 3, W2_Portal_Settings look):
// three cards (the vehicle type, its fares, the meter rates), one label
// above each field, what is wrong under the field, and a sticky save bar.
//
// The writes are exactly the old page's:
// - Add: the same starting record (0 for every rate, the default picture)
//   plus createdAt, convertLanguage(name) (best effort), then
//   editCarType(record, 'Add').
// - Edit: the stored record with the edited fields, without material-table's
//   tableData. A new name first rewrites carType on every driver
//   (updateUserCar) and car (editCar 'Update') that used the old one, after
//   loading the cars list fresh (nothing is written when it cannot load),
//   then convertLanguage(name) (best effort) and editCarType(record, 'Update').
// Every rate is a number, as before: a field the person edited is saved as
// Number(text) (an emptied field is 0); a field nobody touched keeps its
// stored value. Saving needs critical edits on (settings.AllowCriticalEditsAdmin).
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import { api, store } from 'common';
import CircularLoading from '../components/CircularLoading';
import { loadCars, markCarsStale } from '../utils/carsSession';
import { showToast, confirmDialog, InlineMessage } from '../components/feedback';
import { BackButton, PageHeader, SectionCard, SaveBar, FieldLabel, FieldError, calmCaps } from '../components/modern';
import { tokens as c } from '../theme';

// The record a new vehicle type starts from (unchanged from the old page).
const NEW_CAR_TYPE = {
  name: '',
  base_fare: 0,
  convenience_fee_type: '',
  pos: '',
  convenience_fees: 0,
  rate_per_hour: 0,
  extra_info: '',
  min_fare: 0,
  rate_per_unit_distance: 0,
  fleet_admin_fee: 0,
  // Net-of-VAT daily fee for the opt-in flat-rate driver plan.
  // 0 / absent = this car type is not offered on the plan.
  daily_flat_rate: 0,
  // Meter-specific rates (used by Donkey Meter / Offline Meter). Seeded to
  // 0 / "" so they are written on the first save; OfflineFareCalculator reads
  // only these fields, never the regular ones.
  base_fare_meter: 0,
  convenience_fee_type_meter: '',
  convenience_fees_meter: 0,
  rate_per_hour_meter: 0,
  min_fare_meter: 0,
  rate_per_unit_distance_meter: 0,
  rate_per_minute_wait_meter: 0,
  image: 'https://cdn.pixabay.com/photo/2012/04/15/22/09/car-35502__480.png',
};

const TEXT_FIELDS = ['name', 'extra_info'];
const SELECT_FIELDS = ['convenience_fee_type', 'convenience_fee_type_meter'];
const NUMBER_FIELDS = [
  'pos',
  'base_fare',
  'rate_per_unit_distance',
  'rate_per_hour',
  'min_fare',
  'convenience_fees',
  'fleet_admin_fee',
  'daily_flat_rate',
  'base_fare_meter',
  'rate_per_hour_meter',
  'min_fare_meter',
  'rate_per_unit_distance_meter',
  'rate_per_minute_wait_meter',
  'convenience_fees_meter',
];
// Required on every save, as before (0 or empty is not accepted), in the
// old order, with the old messages.
const REQUIRED = [
  ['name', 'proper_input_name'],
  ['pos', 'position_required'],
  ['base_fare', 'base_fare_required'],
  ['min_fare', 'min_fare_required'],
  ['rate_per_hour', 'rate_per_hour_required'],
  ['rate_per_unit_distance', 'rate_per_unit_distance_required'],
  ['convenience_fee_type', 'convenience_fee_type_required'],
];

const asText = (v) => (v === undefined || v === null ? '' : String(v));

export default function EditCarType() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { editCarType, updateUserCar, editCar, convertLanguage, fetchCars } = api;
  const settings = useSelector((s) => s.settingsdata.settings);
  const cartypes = useSelector((s) => s.cartypes);
  const staticusers = useSelector((s) => s.usersdata.staticusers);
  const auth = useSelector((s) => s.auth);
  const langLocale = auth?.profile?.lang?.langLocale ? auth.profile.lang.langLocale : null;

  const [original, setOriginal] = useState(null); // the stored record (or the new-record template)
  const [draft, setDraft] = useState(null); // text and select fields
  const [numText, setNumText] = useState({}); // numeric fields the person edited, as typed
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    if (id) {
      if (cartypes && cartypes.cars) {
        const carData = cartypes.cars.filter((item) => item.id === id.toString())[0];
        if (!carData) {
          navigate('/404');
          return;
        }
        setOriginal(carData);
        setDraft({ ...carData });
        setNumText({});
      }
    } else {
      setOriginal(NEW_CAR_TYPE);
      setDraft({ ...NEW_CAR_TYPE });
      setNumText({});
    }
  }, [cartypes, cartypes.cars, id, navigate]);

  const backToList = () => navigate('/cartypes', { state: { pageNo: state?.pageNo } });

  // ── Values, changes and errors ────────────────────────────────────────
  const numberOf = (key) => {
    if (numText[key] !== undefined) return Number(String(numText[key]).replace(/,/g, '').trim());
    return draft ? draft[key] : undefined;
  };
  const shownNumber = (key) => (numText[key] !== undefined ? numText[key] : asText(draft ? draft[key] : ''));

  const buildRecord = () => {
    const out = { ...draft };
    Object.keys(numText).forEach((key) => {
      out[key] = Number(String(numText[key]).replace(/,/g, '').trim());
    });
    return out;
  };

  const changedKeys = (() => {
    if (!draft || !original) return [];
    const list = [];
    TEXT_FIELDS.concat(SELECT_FIELDS).forEach((key) => {
      if (asText(draft[key]) !== asText(original[key])) list.push(key);
    });
    Object.keys(numText).forEach((key) => {
      if (numberOf(key) !== original[key]) list.push(key);
    });
    return list;
  })();

  const errors = {};
  if (draft) {
    NUMBER_FIELDS.forEach((key) => {
      if (numText[key] === undefined) return;
      const n = numberOf(key);
      if (!Number.isFinite(n)) errors[key] = t('form_number_invalid', { defaultValue: 'Enter a number' });
      else if (n < 0) errors[key] = t('form_number_min', { min: 0, defaultValue: 'Enter {{min}} or more' });
    });
    REQUIRED.forEach(([key, msgKey]) => {
      if (errors[key]) return;
      const v = NUMBER_FIELDS.includes(key) ? numberOf(key) : draft[key];
      if (!v) errors[key] = t(msgKey);
    });
  }
  const errorKeys = Object.keys(errors);
  const shown = (key) => (showErrors || numText[key] !== undefined || (draft && asText(draft[key]) !== asText(original && original[key])) ? errors[key] : null);

  const jumpToError = () => {
    setShowErrors(true);
    const first = TEXT_FIELDS.concat(SELECT_FIELDS, NUMBER_FIELDS).find((key) => errors[key]);
    if (first) {
      const el = document.getElementById(`ect-${first}`);
      if (el && typeof el.focus === 'function') el.focus();
    }
  };

  const discard = async () => {
    const ok = await confirmDialog({
      title: t('form_discard_title', { defaultValue: 'Discard your changes?' }),
      body: t('form_discard_body', { defaultValue: 'What you typed here is not saved.' }),
      confirmLabel: t('settings_discard', { defaultValue: 'Discard' }),
      cancelLabel: t('confirm_cancel', { defaultValue: 'Cancel' }),
      destructive: true,
    });
    if (!ok) return;
    setDraft({ ...original });
    setNumText({});
    setShowErrors(false);
    setFormError(null);
  };

  // ── Save ──────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (saving || !draft) return;
    setShowErrors(true);
    setFormError(null);
    if (errorKeys.length) {
      jumpToError();
      return;
    }
    if (!settings.AllowCriticalEditsAdmin) {
      showToast.warning(t('demo_mode'));
      return;
    }
    const data = buildRecord();
    setSaving(true);
    try {
      if (!id) {
        data.createdAt = new Date().getTime();
        // Translation is best-effort: a failed languages read must never leave
        // the save half-applied or the button stuck.
        try {
          await convertLanguage(data.name, langLocale);
        } catch (_) {
          /* best-effort */
        }
        dispatch(editCarType(data, 'Add'));
        showToast.success(t('saved', { defaultValue: 'Saved' }));
        navigate('/cartypes');
        return;
      }
      if (!changedKeys.length) {
        setSaving(false);
        setFormError(t('make_changes_to_update'));
        return;
      }
      delete data.tableData;
      if (data.name !== original.name && staticusers) {
        // A rename rewrites carType on every matching driver AND car. Load the
        // cars list fresh BEFORE the first write: if it cannot be loaded, stop
        // with nothing written instead of renaming the drivers and then failing
        // on the cars (a half-applied rename breaks their matching).
        markCarsStale();
        await loadCars(dispatch, fetchCars, auth.profile.uid);
        const allCars = store.getState().carlistdata.cars;
        if (!allCars) {
          setSaving(false);
          setFormError(t('cars_load_failed_no_change', { defaultValue: 'Could not load the cars list, so nothing was changed. Please try again.' }));
          return;
        }
        const users = staticusers && staticusers.filter((user) => user.usertype === 'driver' && user.carType === original.name);
        for (let i = 0; i < users?.length; i++) {
          dispatch(updateUserCar(users[i].id, { carType: data.name }));
        }
        const cars = allCars.filter((car) => car.carType === original.name);
        for (let i = 0; i < cars.length; i++) {
          dispatch(editCar({ ...cars[i], carType: data.name }, 'Update'));
        }
        // EDIT_CAR is a no-op reducer: make the cars list refetch once.
        if (cars.length > 0) markCarsStale();
      }
      // Translation is best-effort (see above).
      try {
        await convertLanguage(data.name, langLocale);
      } catch (_) {
        /* best-effort */
      }
      dispatch(editCarType(data, 'Update'));
      showToast.success(t('saved', { defaultValue: 'Saved' }));
      backToList();
    } catch (e) {
      setSaving(false);
      setFormError(e && e.message ? e.message : t('form_save_failed', { defaultValue: 'Could not save' }));
    }
  };

  if (!draft) return <CircularLoading />;

  // ── Fields ────────────────────────────────────────────────────────────
  const symbol = settings.symbol || '';
  const feeTypeOptions = [
    { value: 'flat', label: t('flat') },
    { value: 'percentage', label: t('percentage') },
  ];
  const fieldBox = (key, label, control) => (
    <Box key={key} sx={{ minWidth: 0 }}>
      <FieldLabel htmlFor={`ect-${key}`} changed={changedKeys.includes(key)}>
        {label}
      </FieldLabel>
      {control}
      <FieldError id={`ect-${key}-error`}>{shown(key)}</FieldError>
    </Box>
  );
  const textField = (key, label) =>
    fieldBox(
      key,
      label,
      <TextField
        id={`ect-${key}`}
        fullWidth
        value={asText(draft[key])}
        disabled={saving}
        error={!!shown(key)}
        onChange={(e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }))}
        inputProps={{ 'aria-describedby': shown(key) ? `ect-${key}-error` : undefined, autoComplete: 'off' }}
      />
    );
  const numberField = (key, label, adornment) =>
    fieldBox(
      key,
      label,
      <TextField
        id={`ect-${key}`}
        fullWidth
        value={shownNumber(key)}
        disabled={saving}
        error={!!shown(key)}
        onChange={(e) => {
          const v = e.target.value;
          setNumText((prev) => ({ ...prev, [key]: v }));
        }}
        inputProps={{
          inputMode: 'decimal',
          autoComplete: 'off',
          'aria-describedby': shown(key) ? `ect-${key}-error` : undefined,
          'aria-invalid': shown(key) ? 'true' : undefined,
        }}
        InputProps={
          adornment
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <Box component="span" sx={{ color: c.INK_2, fontWeight: 600 }}>
                      {adornment}
                    </Box>
                  </InputAdornment>
                ),
              }
            : undefined
        }
        sx={{ '& input': { fontVariantNumeric: 'tabular-nums' } }}
      />
    );
  const selectField = (key, label) =>
    fieldBox(
      key,
      label,
      <TextField
        id={`ect-${key}`}
        select
        fullWidth
        value={asText(draft[key])}
        disabled={saving}
        error={!!shown(key)}
        onChange={(e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }))}
        SelectProps={{ SelectDisplayProps: { 'aria-describedby': shown(key) ? `ect-${key}-error` : undefined } }}
      >
        {feeTypeOptions.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>
    );
  const feeAdornment = (typeKey) => (draft[typeKey] === 'percentage' ? '%' : draft[typeKey] === 'flat' ? symbol : '');
  // The fields flow into more columns as the screen grows: one on a phone,
  // two from md (as before), three from lg and four from xl, so no field is
  // stretched across a wide screen. All three cards pass `long` so their
  // field edges line up at xl.
  const grid = (children, long) => (
    <Box
      sx={{
        display: 'grid',
        columnGap: 2.5,
        rowGap: 2.25,
        gridTemplateColumns: {
          xs: '1fr',
          md: 'repeat(2, minmax(0, 1fr))',
          lg: 'repeat(3, minmax(0, 1fr))',
          ...(long ? { xl: 'repeat(4, minmax(0, 1fr))' } : {}),
        },
        mt: 1.5,
      }}
    >
      {children}
    </Box>
  );

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <BackButton onClick={backToList} />
      <PageHeader title={calmCaps(t(id ? 'update_carType_title' : 'add_carType_title'))} />
      {formError ? (
        <InlineMessage kind="error" onClose={() => setFormError(null)}>
          {formError}
        </InlineMessage>
      ) : null}

      <SectionCard id="ect-type" icon={<DirectionsCarFilledOutlinedIcon />} hue="accent" title={calmCaps(t('car_type_title'))}>
        {grid([
          textField('name', t('name')),
          numberField('pos', t('position')),
          <Box key="extra" sx={{ gridColumn: { md: 'span 2', lg: 'auto' }, minWidth: 0 }}>
            {textField('extra_info', t('extra_info'))}
          </Box>,
        ], true)}
      </SectionCard>

      <SectionCard id="ect-fares" icon={<PaymentsOutlinedIcon />} hue="success" title={t('car_types_fares', { defaultValue: 'Fares' })}>
        {grid([
          numberField('base_fare', t('base_fare'), symbol),
          numberField('min_fare', t('min_fare'), symbol),
          numberField('rate_per_unit_distance', t('rate_per_unit_distance'), symbol),
          numberField('rate_per_hour', t('rate_per_hour'), symbol),
          selectField('convenience_fee_type', t('convenience_fee_type')),
          numberField('convenience_fees', t('convenience_fee'), feeAdornment('convenience_fee_type')),
          numberField('fleet_admin_fee', t('fleet_admin_fee')),
          numberField('daily_flat_rate', t('daily_flat_rate', { defaultValue: 'Daily flat rate (incl. VAT)' }), symbol),
        ], true)}
      </SectionCard>

      <SectionCard
        id="ect-meter"
        icon={<SpeedOutlinedIcon />}
        hue="info"
        title={t('meter_rates_section', { defaultValue: 'Meter Rates (Donkey Meter / Offline Meter)' })}
      >
        {grid([
          numberField('base_fare_meter', t('base_fare_meter', { defaultValue: 'Base fare (meter)' }), symbol),
          numberField('min_fare_meter', t('min_fare_meter', { defaultValue: 'Minimum fare (meter)' }), symbol),
          numberField('rate_per_unit_distance_meter', t('rate_per_unit_distance_meter', { defaultValue: 'Rate per km (meter)' }), symbol),
          numberField('rate_per_hour_meter', t('rate_per_hour_meter', { defaultValue: 'Rate per hour (meter)' }), symbol),
          numberField('rate_per_minute_wait_meter', t('rate_per_minute_wait_meter', { defaultValue: 'Waiting Rate Per Minute (Meter)' }), symbol),
          selectField('convenience_fee_type_meter', t('convenience_fee_type_meter', { defaultValue: 'Convenience fee type (meter)' })),
          numberField('convenience_fees_meter', t('convenience_fee_meter', { defaultValue: 'Convenience fee (meter)' }), feeAdornment('convenience_fee_type_meter')),
        ], true)}
      </SectionCard>

      <SaveBar
        changedCount={changedKeys.length}
        errorCount={showErrors ? errorKeys.length : 0}
        onJumpToError={jumpToError}
        onDiscard={discard}
        onSave={handleSubmit}
        saving={saving}
        saveLabel={id ? t('update', { defaultValue: 'Update' }) : calmCaps(t('submit'))}
        sx={{ border: `1px solid ${c.OUTLINE}`, borderRadius: '12px' }}
      />
    </Box>
  );
}
